import React, { useState, useEffect } from 'react';
import { PublicLandingPage } from './pages/PublicLandingPage';
import { StandalonePuckEditor } from './pages/StandalonePuckEditor';
import { PublicFragebogenPage } from './pages/PublicFragebogenPage';
import { ErrorPage } from './pages/ErrorPage';
import campaignService from '../services/CampaignService';
import pb from '../pocketbaseClient';

// Simple router component that handles URL-based routing
export function Router({ children }) {
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const [isPublicRoute, setIsPublicRoute] = useState(false);
  const [campaignStatus, setCampaignStatus] = useState(null); // null = not checked, 'online' = online, 'offline' = offline, 'not_found' = not found

  // Function to check campaign status
  const checkCampaignStatus = async (path) => {
    const publicRoutePattern = /^\/([^/]+)\/([^/]+)(?:\/([^/]+))?\/?$/;
    const fragebogenRoutePattern = /^\/apply\/([^/]+)\/?$/;
    
    const publicMatch = path.match(publicRoutePattern);
    const fragebogenMatch = path.match(fragebogenRoutePattern);
    
    if (publicMatch) {
      // Check landing page campaign status
      const [, companyName, campaignName, landingPageName] = publicMatch;
      
      // Decode URL-encoded parameters to handle whitespaces and special characters
      const decodedCompanyName = decodeURIComponent(companyName);
      const decodedCampaignName = decodeURIComponent(campaignName);
      const decodedLandingPageName = decodeURIComponent(landingPageName);
      
      try {
        const campaignData = await campaignService.getCampaignByRoute(decodedCompanyName, decodedCampaignName, decodedLandingPageName);
        if (campaignData.success) {
          setCampaignStatus(campaignData.data.status === 'offline' ? 'offline' : 'online');
        } else {
          setCampaignStatus('not_found');
        }
      } catch (error) {
        console.error('Error checking campaign status:', error);
        setCampaignStatus('not_found');
      }
    } else if (fragebogenMatch) {
      // Check quiz campaign status
      const [, campaignId] = fragebogenMatch;
      try {
        const campaign = await campaignService.getCampaignById(campaignId);
        if (campaign.success) {
          setCampaignStatus(campaign.data.status === 'offline' ? 'offline' : 'online');
        } else {
          setCampaignStatus('not_found');
        }
      } catch (error) {
        console.error('Error checking campaign status:', error);
        setCampaignStatus('not_found');
      }
    } else {
      setCampaignStatus(null);
    }
  };

  useEffect(() => {
    const handlePopState = () => {
      setCurrentPath(window.location.pathname);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    // Check if current path is a public landing page route
    const publicRoutePattern = /^\/([^/]+)\/([^/]+)(?:\/([^/]+))?\/?$/;
    const editRoutePattern = /^\/([^/]+)\/([^/]+)(?:\/([^/]+))?\/edit\/?$/;
    const fragebogenRoutePattern = /^\/apply\/([^/]+)\/?$/;
    
    const publicMatch = currentPath.match(publicRoutePattern);
    const editMatch = currentPath.match(editRoutePattern);
    const fragebogenMatch = currentPath.match(fragebogenRoutePattern);
    
    if (editMatch) {
      // Edit mode: /company/campaign/landingpage/edit - require authentication (admin route)
      setIsPublicRoute(false);
      setCampaignStatus(null);
      return;
    }
    
    if (fragebogenMatch) {
      // Apply mode: /apply/campaignId - check campaign status (public route)
      setIsPublicRoute(true);
      checkCampaignStatus(currentPath);
      return;
    }
    
    if (publicMatch) {
      // Public view mode: /company/campaign/landingpage - check campaign status (public route)
      setIsPublicRoute(true);
      checkCampaignStatus(currentPath);
      return;
    }
    
    // Admin routes
    setIsPublicRoute(false);
    setCampaignStatus(null);
  }, [currentPath]);

  // Handle saving landing page data
  const handleSaveLandingPage = async (companyName, campaignName, landingPageName, landingPageData) => {
    try {
      // Find campaign by route (company_name, campaign_name, landing_page_name)
      const campaignResult = await campaignService.getCampaignByRoute(companyName, campaignName, landingPageName);
      
      if (campaignResult.success) {
        const campaign = campaignResult.data;
        
        // Update the campaign with landing page data
        await campaignService.updateCampaign(campaign.id, {
          landing_page_data: landingPageData
        });
        console.log('Landing page saved successfully');
      } else {
        console.error('Campaign not found for route:', { companyName, campaignName, landingPageName });
        alert('Kampagne nicht gefunden. Bitte überprüfen Sie die URL-Route.');
      }
    } catch (error) {
      console.error('Error saving landing page:', error);
      alert('Fehler beim Speichern der Landing Page: ' + error.message);
    }
  };

  // Check for edit routes first (these are admin routes that need special handling)
  const editRoutePattern = /^\/([^/]+)\/([^/]+)(?:\/([^/]+))?\/edit\/?$/;
  const editMatch = currentPath.match(editRoutePattern);
  
  if (editMatch) {
    // Edit mode: /company/campaign/landingpage/edit - check authentication first
    const [, companyName, campaignName, landingPageName] = editMatch;
    
    // Decode URL-encoded parameters to handle whitespaces and special characters
    const decodedCompanyName = decodeURIComponent(companyName);
    const decodedCampaignName = decodeURIComponent(campaignName);
    const decodedLandingPageName = decodeURIComponent(landingPageName);
    
    // Check if user is authenticated
    if (!pb.authStore.isValid) {
      return (
        <ErrorPage
          title="Kampagne nicht verfügbar"
          message="Diese Kampagne wurde nicht gefunden."
          subtitle="Mögliche Gründe: Die Kampagne wurde beendet, ist offline oder der Link ist nicht mehr gültig."
          errorCode="404"
          errorType="Not Found"
          showBackButton={true}
        />
      );
    }
    
    return (
      <StandalonePuckEditor
        companyName={decodedCompanyName}
        campaignName={decodedCampaignName}
        landingPageName={decodedLandingPageName}
        onBack={() => window.history.back()}
        onSave={(data) => handleSaveLandingPage(decodedCompanyName, decodedCampaignName, decodedLandingPageName, data)}
        onManualSave={(data) => handleSaveLandingPage(decodedCompanyName, decodedCampaignName, decodedLandingPageName, data)}
        onEditRoute={() => {
          // Navigate back to the campaign management page to edit the route
          window.location.href = '/?page=bewerbungspool';
        }}
        onImageUpload={(uploadedImage) => {
          console.log('Image uploaded successfully:', uploadedImage);
          // You can handle the uploaded image here
          // For example, show a notification or add it to a gallery
        }}
      />
    );
  }

  // If it's a public route, render the appropriate component
  if (isPublicRoute) {
    const publicRoutePattern = /^\/([^/]+)\/([^/]+)(?:\/([^/]+))?\/?$/;
    const fragebogenRoutePattern = /^\/apply\/([^/]+)\/?$/;
    
    const publicMatch = currentPath.match(publicRoutePattern);
    const fragebogenMatch = currentPath.match(fragebogenRoutePattern);
    
    // Show 404 for offline or not found campaigns
    if (campaignStatus === 'offline' || campaignStatus === 'not_found') {
      return (
        <ErrorPage
          title="Kampagne nicht verfügbar"
          message={campaignStatus === 'offline' 
            ? 'Diese Kampagne ist derzeit offline und nicht verfügbar.'
            : 'Diese Kampagne wurde nicht gefunden.'}
          subtitle="Mögliche Gründe: Die Kampagne wurde beendet, ist offline oder der Link ist nicht mehr gültig."
          errorCode="404"
          errorType="Not Found"
          showBackButton={true}
        />
      );
    }
    
    // Show loading while checking campaign status
    if (campaignStatus === null && (publicMatch || fragebogenMatch)) {
      return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center font-sf-pro">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 dark:border-indigo-400 mx-auto mb-4"></div>
            <p className="text-slate-600 dark:text-slate-300">Lade Kampagne...</p>
          </div>
        </div>
      );
    }
    
    if (fragebogenMatch) {
      // Apply mode
      const [, campaignId] = fragebogenMatch;
      return (
        <PublicFragebogenPage
          campaignId={campaignId}
        />
      );
    }
    
    if (publicMatch) {
      // Public view mode
      const [, companyName, campaignName, landingPageName] = publicMatch;
      
      // Decode URL-encoded parameters to handle whitespaces and special characters
      const decodedCompanyName = decodeURIComponent(companyName);
      const decodedCampaignName = decodeURIComponent(campaignName);
      const decodedLandingPageName = decodeURIComponent(landingPageName);
      
      return (
        <PublicLandingPage
          match={{
            params: {
              companyName: decodedCompanyName,
              campaignName: decodedCampaignName,
              landingPageName: decodedLandingPageName
            }
          }}
        />
      );
    }
  }

  // Render admin interface for all other routes
  return children;
}

// Helper function to navigate to a landing page
export const navigateToLandingPage = (companyName, campaignName, landingPageName = 'landing', edit = false) => {
  const path = edit 
    ? `/${companyName}/${campaignName}/${landingPageName}/edit`
    : `/${companyName}/${campaignName}/${landingPageName}`;
  
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
};

// Helper function to navigate to admin
export const navigateToAdmin = () => {
  window.history.pushState({}, '', '/admin');
  window.dispatchEvent(new PopStateEvent('popstate'));
};

// Helper function to get fragebogen URL
export const getFragebogenUrl = (campaignId) => {
  return `https://karrieresichern.de/apply/${campaignId}`;
};

// Helper function to get landing page URL
export const getLandingPageUrl = (companyName, campaignName, landingPageName = 'landing') => {
  return `https://karrieresichern.de/${companyName}/${campaignName}/${landingPageName}`;
};
